import { useCallback, useEffect, useState } from 'react';

import { ApiError, type MessageSummary, api } from '../lib/api';

export interface MessagesState {
  readonly messages: readonly MessageSummary[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly hasMore: boolean;
  readonly loadingMore: boolean;
  readonly refresh: () => void;
  readonly loadMore: () => void;
  readonly markSeenLocally: (id: string) => void;
  readonly removeLocally: (id: string) => void;
}

/**
 * Loads one inbox's messages, newest first, with keyset pagination.
 *
 * Refreshing replaces only the first page and drops any pages that were loaded
 * beyond it. That is deliberate: merging new arrivals into an already-paginated
 * list means reconciling two different windows of a list that is changing
 * underneath, and the case it would serve — new mail arriving while you are
 * reading page four — is not worth that complexity here.
 */
export function useMessages(
  token: string | null,
  address: string | null,
  onUnauthorized: () => void,
): MessagesState {
  const [messages, setMessages] = useState<readonly MessageSummary[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFailure = useCallback(
    (cause: unknown): void => {
      if (cause instanceof ApiError && cause.unauthorized) {
        onUnauthorized();
        return;
      }

      setError(cause instanceof ApiError ? cause.message : 'Could not reach the server.');
    },
    [onUnauthorized],
  );

  const refresh = useCallback((): void => {
    if (!token || !address) return;

    void api
      .listMessages(token, address)
      .then((page) => {
        setMessages(page.items);
        setCursor(page.nextCursor);
        setError(null);
      })
      .catch(handleFailure)
      .finally(() => setLoading(false));
  }, [token, address, handleFailure]);

  useEffect(() => {
    if (!address) {
      setMessages([]);
      setCursor(null);
      return;
    }

    // Cleared rather than left in place while the next inbox loads, so
    // switching inboxes never shows another inbox's mail under the new name.
    setMessages([]);
    setCursor(null);
    setLoading(true);
    refresh();
  }, [address, refresh]);

  const loadMore = useCallback((): void => {
    if (!token || !address || !cursor || loadingMore) return;

    setLoadingMore(true);

    void api
      .listMessages(token, address, cursor)
      .then((page) => {
        setMessages((current) => [...current, ...page.items]);
        setCursor(page.nextCursor);
      })
      .catch(handleFailure)
      .finally(() => setLoadingMore(false));
  }, [token, address, cursor, loadingMore, handleFailure]);

  /**
   * Flips the unread flag in the list without refetching.
   *
   * The server call is fired by the caller; this only keeps the row in step so
   * the bold weight clears the instant the message is opened.
   */
  const markSeenLocally = useCallback((id: string): void => {
    setMessages((current) =>
      current.map((message) => (message.id === id ? { ...message, seen: true } : message)),
    );
  }, []);

  const removeLocally = useCallback((id: string): void => {
    setMessages((current) => current.filter((message) => message.id !== id));
  }, []);

  return {
    messages,
    loading,
    error,
    hasMore: cursor !== null,
    loadingMore,
    refresh,
    loadMore,
    markSeenLocally,
    removeLocally,
  };
}
